<template>
    <div class="reports-view">
        <div class="container-fluid px-4 py-4">
            <!-- Header -->
            <div class="mb-5">
                <h2 class="color-primary-4 font-weight-bold">Reportes</h2>
                <div class="text-muted font-14">
                    Gestión de reportes del sistema
                </div>
            </div>

            <!-- Admin Access Check -->
            <div v-if="!isUserRoleAdmin" class="alert alert-warning">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                No tiene permisos para acceder a los reportes.
            </div>

            <!-- Reports Content -->
            <div v-else>
                <!-- Reports Menu Grid -->
                <div class="row">
                    <!-- Dashboard Report -->
                    <div class="col-md-4 mb-4">
                        <div class="report-card" @click="onGenerateReport('dashboard')">
                            <div class="icon-wrapper bg-primary-light">
                                <i class="fas fa-tachometer-alt fa-2x color-primary-4"></i>
                            </div>
                            <h5 class="font-weight-bold mt-3 mb-2">Tablero de Control</h5>
                            <p class="text-muted font-14 mb-0">
                                Visualización gráfica de indicadores clave
                            </p>
                        </div>
                    </div>

                    <!-- Statistics Report -->
                    <div class="col-md-4 mb-4">
                        <div class="report-card" @click="onGenerateReport('statistics')">
                            <div class="icon-wrapper bg-success-light">
                                <i class="fas fa-chart-bar fa-2x color-success"></i>
                            </div>
                            <h5 class="font-weight-bold mt-3 mb-2">Reporte de Estadísticas</h5>
                            <p class="text-muted font-14 mb-0">
                                Generar reporte de estadísticas del sistema
                            </p>
                        </div>
                    </div>

                    <!-- Authorized Requests Report -->
                    <div class="col-md-4 mb-4">
                        <div class="report-card" @click="navigateTo('authorizedRequestsReport')">
                            <div class="icon-wrapper bg-info-light">
                                <i class="fas fa-check-circle fa-2x color-info"></i>
                            </div>
                            <h5 class="font-weight-bold mt-3 mb-2">Solicitudes Autorizadas</h5>
                            <p class="text-muted font-14 mb-0">
                                Listado detallado de solicitudes autorizadas
                            </p>
                        </div>
                    </div>

                    <!-- Technical Efficiency Report -->
                    <div class="col-md-4 mb-4">
                        <div class="report-card" @click="onGenerateReport('efficiency')">
                            <div class="icon-wrapper bg-warning-light">
                                <i class="fas fa-cogs fa-2x color-warning"></i>
                            </div>
                            <h5 class="font-weight-bold mt-3 mb-2">Eficiencia Técnica</h5>
                            <p class="text-muted font-14 mb-0">
                                Reporte de eficiencia técnica del sistema
                            </p>
                        </div>
                    </div>

                    <!-- Services Excel Report -->
                    <div class="col-md-4 mb-4">
                        <div class="report-card" @click="onGenerateReport('services')">
                            <div class="icon-wrapper bg-purple-light">
                                <i class="fas fa-file-excel fa-2x color-purple"></i>
                            </div>
                            <h5 class="font-weight-bold mt-3 mb-2">Reporte de Servicios (Excel)</h5>
                            <p class="text-muted font-14 mb-0">
                                Exportación de servicios a formato Excel
                            </p>
                        </div>
                    </div>

                    <!-- History Report -->
                    <div class="col-md-4 mb-4">
                        <div class="report-card" @click="onGenerateReport('history')">
                            <div class="icon-wrapper bg-danger-light">
                                <i class="fas fa-history fa-2x color-danger"></i>
                            </div>
                            <h5 class="font-weight-bold mt-3 mb-2">Historial de Reclamo</h5>
                            <p class="text-muted font-14 mb-0">
                                Historial detallado por número de reclamo
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <reports-params-modal ref="paramsModal" @generate="onGenerateWithParams" />
    </div>
</template>

<script>
//mixins
import { userMixin } from '@/mixins/user.mixin.js';
import loadingMixin from '@quisit/mipe-utils/mixins/loading';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

//components
import ReportsParamsModal from './components/ReportsParamsModal.vue';

//services
import {
    getStatisticsReport,
    getDashboardReport,
    getEficienciaTecnicaReport,
    getServiciosExcelReport,
    getHistorialReport
} from '@/services/report.api.js';

//utils
import { i18n } from '@/i18n.js';
import { convertToBlob } from '@/utils/utilities';

export default {
    name: 'ReportsView',
    mixins: [userMixin, loadingMixin, alertMixin],
    components: {
        ReportsParamsModal
    },
    mounted() {
        // Set sitemap breadcrumb
        this.$store.commit('app/SET_SITEMAP_INFO', {
            routes: [i18n.t('Home'), 'Reportes'],
        });
    },
    methods: {
        navigateTo(routeName) {
            this.$router.push({ name: routeName });
        },
        async onGenerateReport(type) {
            if (type === 'statistics') {
                // Statistics report doesn't need parameters
                await this.executeReportGeneration(type);
            } else {
                // Open modal for other reports
                this.$refs.paramsModal.showModal(type);
            }
        },
        async onGenerateWithParams({ type, params }) {
            await this.executeReportGeneration(type, params);
        },
        async executeReportGeneration(type, params = {}) {
            try {
                this.ShowWait();
                let response;

                switch (type) {
                    case 'statistics':
                        response = await getStatisticsReport();
                        break;
                    case 'dashboard':
                        response = await getDashboardReport(
                            this.formatDate(params.dateFrom),
                            this.formatDate(params.dateTo)
                        );
                        break;
                    case 'efficiency':
                        response = await getEficienciaTecnicaReport(
                            this.formatDate(params.dateFrom),
                            this.formatDate(params.dateTo)
                        );
                        break;
                    case 'services':
                        response = await getServiciosExcelReport(
                            this.formatDate(params.dateFrom),
                            this.formatDate(params.dateTo)
                        );
                        break;
                    case 'history':
                        response = await getHistorialReport(params.claimId);
                        break;
                }

                if (response && response.data) {
                    convertToBlob(response.data);
                }
                this.HideWait();
            } catch (error) {
                this.HideWait();
                this.ShowToast(
                    i18n.t('Error'),
                    i18n.t('ErrorGeneratingReport'),
                    'error'
                );
                console.error(error);
            }
        },
        formatDate(date) {
            if (!date) return undefined;
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/scss/bootstrap/_colors.scss';

.reports-view {
    min-height: calc(100vh - 200px);
}

.report-card {
    padding: 30px 20px;
    background: #fff;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    text-align: center;
    height: 100%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
    border: 1px solid transparent;

    &:hover {
        box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
        transform: translateY(-5px);
        border-color: map-get($colors, 'primary-4');

        .icon-wrapper {
            transform: scale(1.1);
        }
    }
}

.icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 15px;
    transition: transform 0.3s ease;
}

// Custom background colors for icons
.bg-primary-light {
    background-color: rgba(map-get($colors, 'primary-4'), 0.1);
}

.bg-success-light {
    background-color: rgba(#28a745, 0.1);
}

.bg-info-light {
    background-color: rgba(#17a2b8, 0.1);
}

.bg-warning-light {
    background-color: rgba(#ffc107, 0.1);
}

.bg-danger-light {
    background-color: rgba(#dc3545, 0.1);
}

.bg-purple-light {
    background-color: rgba(#6f42c1, 0.1);
}

// Custom colors
.color-success {
    color: #28a745;
}

.color-info {
    color: #17a2b8;
}

.color-warning {
    color: #ffc107;
}

.color-danger {
    color: #dc3545;
}

.color-purple {
    color: #6f42c1;
}
</style>
